import React, { useMemo } from "react";
import { Layers } from "lucide-react";

import { Card } from "@/components/ui/Card";
import UsageRankedBars from "./UsageRankedBars";
import UsageSectionHeader from "./UsageSectionHeader";
import { fmt, fmtPct, pctOf } from "./format";

/**
 * Tools, ranked, then listed. The first card of the usage report — see
 * `UsageSummary.jsx` for how the three read together.
 *
 * The chart and the list are the same numbers twice, and both earn their place:
 * the bars answer "what dominates" without arithmetic, and the list carries the
 * sub-items, which no bar can. The chart is dropped below two tools, where
 * ranking is not a question anyone has.
 */
const ToolUsageCard = ({ modules = [], title = "Tool usage", emptyHint }) => {
  const totalUses = modules.reduce((sum, mod) => sum + Number(mod.uses ?? 0), 0);
  const chartData = useMemo(
    () =>
      modules
        .map((mod) => ({ id: mod.module, name: mod.name, value: Number(mod.uses ?? 0) }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 10),
    [modules],
  );

  return (
    <Card className="p-5">
      <UsageSectionHeader
        icon={Layers}
        title={title}
        subtitle="One count per invocation, and the sub-items always add up to it. A tool that fans out counts each sub-item separately — three boolean platforms in one request are three uses. A tool whose uses mean different things splits the same way: candidate matching separates the run that follows an application from a re-analysis someone asked for. Runs served entirely from cache are not counted at all."
        aside={
          modules.length > 0 ? (
            <span className="whitespace-nowrap rounded-full border border-border-primary px-2.5 py-1 text-[11px] text-text-secondary tabular-nums">
              {fmt(totalUses)} uses
            </span>
          ) : null
        }
      />

      {modules.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border-primary p-8 text-center text-sm text-text-secondary">
          {emptyHint || "No tool usage recorded in this period."}
        </p>
      ) : (
        <>
          {chartData.length > 2 && (
            <div className="mb-5 border-b border-border-primary pb-4">
              <UsageRankedBars data={chartData} valueLabel="Uses" total={totalUses} />
            </div>
          )}

          <div className="flex flex-col divide-y divide-border-primary/70">
            {modules.map((mod) => (
              <div key={mod.module} className="py-2.5">
                <div className="flex items-baseline justify-between gap-4">
                  <span className="text-sm font-medium text-text-primary">{mod.name}</span>
                  <span className="whitespace-nowrap text-sm text-text-secondary tabular-nums">
                    {fmt(mod.uses)} {Number(mod.uses) === 1 ? "use" : "uses"}
                    <span className="ml-2 text-[11px] text-text-secondary">{fmtPct(pctOf(mod.uses, totalUses))}</span>
                  </span>
                </div>
                {(mod.subItems ?? []).length > 0 && (
                  <ul className="mt-1.5 flex flex-col gap-1 pl-4">
                    {/* `└─` only on the last row. Drawn on every row, the list
                        read as a column of separate one-item trees. */}
                    {mod.subItems.map((item, index) => (
                      <li key={`${mod.module}:${item.key}`} className="flex items-baseline justify-between gap-4 text-[12px] text-text-secondary">
                        <span>
                          {index === mod.subItems.length - 1 ? "└─" : "├─"} {item.label}
                        </span>
                        <span className="tabular-nums">{fmt(item.uses)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
};

export default ToolUsageCard;
